import React from "react";
import moment from "moment";
import { round, isEqual } from "lodash";
const Chartist = process.browser && require("chartist");

import { getChartData } from "../../../../utils/exercise-history";
import { WEIGHT_UNITS } from "../../../../constants";
import {
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import convertToRiR from "../../../../utils/converters/convert-intensity";

const chartTypes = [
  {
    value: "e1rm",
    label: "e1RM",
  },
  {
    value: "load",
    label: "Max Weight",
  },
  {
    value: "volume",
    label: "Daily Volume",
  },
  {
    value: "weeklyvolume",
    label: "Weekly Volume",
  },
  {
    value: "lifts",
    label: "# of Lifts",
  },
  {
    value: "sets",
    label: "# of Sets",
  },
  {
    value: "avgIntensity",
    label: "Avg %1RM",
  },
  {
    value: "peakIntensity",
    label: "Top %1RM",
  },
  {
    value: "missedSets",
    label: "Missed Sets",
  },
  {
    value: "loadVelocity",
    label: "Load-Velocity Profile",
  },
  {
    value: "v1rm",
    label: "v1RM",
  },
];

class HistoryChart extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      activeChart: "e1rm",
      tooltip: {
        show: false,
        y: 0,
        x: 0,
        content: "",
      },
      chartData: {
        data: [],
        minIdx: 0,
        maxIdx: 0,
      },
      unitLabel: {
        tooltipUnit: WEIGHT_UNITS[props.units],
        axisYUnit: WEIGHT_UNITS[props.units],
      },
    };
    this.chartObject = null;

    this.toggleActiveChart = this.toggleActiveChart.bind(this);
  }

  componentDidMount() {
    const {
      exercises,
      units,
      fromMetric,
      from,
      to,
      activeUser,
      athlete,
      isRiR,
    } = this.props;
    const { activeChart, unitLabel } = this.state;
    const chartData = getChartData(
      exercises,
      fromMetric,
      activeChart,
      units,
      "day",
      from,
      to,
      activeUser,
      athlete,
      isRiR
    );
    const options = this.getChartOptions(unitLabel);

    const setTooltip = (props) => {
      const { y = 0, x = 0, show = false, content = "" } = props;
      this.setState({
        tooltip: {
          show,
          x,
          y,
          content,
        },
      });
    };

    this.chartObject = new Chartist.Line(
      ".graph",
      {
        series: [{ data: chartData.data }],
      },
      options
    ).on("draw", function (data) {
      if (data.type === "point") {
        data.element._node.addEventListener("mouseenter", ({ target }) => {
          const [_, value] = target.getAttribute("ct:value").split(",");
          const content = `
            <div>${data.meta}</div>
            <span><b>${value}</b></span>
          `;
          setTooltip({
            show: true,
            x: target.getAttribute("x1") - 55,
            y: target.getAttribute("y2") - 55,
            content,
          });
        });
        data.element._node.addEventListener("mouseleave", () => {
          setTooltip({
            show: false,
          });
        });
      }
      if (data.type === "grid") {
        data.element.attr({
          style:
            "stroke: var(--white-10); stroke-dasharray: 5px 5px; stroke-width:1px",
        });
      }
    });
    this.setState({ chartData });
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      exercises,
      units,
      fromMetric,
      from,
      to,
      activeUser,
      athlete,
      isRiR,
    } = this.props;
    const { activeChart } = this.state;
    const period = activeChart.includes("weekly") ? "week" : "day";
    if (
      !isEqual(prevProps.exercises, exercises) ||
      prevState.activeChart !== activeChart ||
      (prevProps.from !== from && period === "week")
    ) {
      const chartData = getChartData(
        exercises,
        fromMetric,
        activeChart,
        units,
        period,
        from,
        to,
        activeUser,
        athlete,
        isRiR
      );
      const unitLabel = this.getUnitLabel();
      const options = this.getChartOptions(unitLabel);
      this.chartObject.update(
        {
          series: [{ data: chartData.data }],
        },
        options
      );
      this.setState({ chartData, unitLabel });
    }
  }

  toggleActiveChart(e) {
    this.setState({ activeChart: e.currentTarget.value });
  }

  getChartOptions(unitLabel) {
    return {
      height: 270,
      showLine: true,
      showPoint: true,
      fullWidth: true,
      lineSmooth: false,
      chartPadding: { left: -5, bottom: -20 },
      axisX: {
        showGrid: false,
      },
      axisY: {
        labelOffset: {
          y: 5,
          x: 5,
        },
        onlyInteger: true,
      },
      axisX: {
        type: Chartist.FixedScaleAxis,
        divisor: 5,
        showGrid: false,
        showLabel: false,
      },
    };
  }

  getUnitLabel() {
    const { activeChart } = this.state;
    const { units } = this.props;

    if (["sets", "missedSets"].includes(activeChart)) {
      return { tooltipUnit: "Sets", axisYUnit: "" };
    } else if (["lifts"].includes(activeChart)) {
      return { tooltipUnit: "Reps", axisYUnit: "" };
    } else if (["peakIntensity", "avgIntensity"].includes(activeChart)) {
      return { tooltipUnit: "%", axisYUnit: "%" };
    } else if (["loadVelocity", "rpeVelocity"].includes(activeChart)) {
      return { tooltipUnit: "m/s", axisYUnit: "m/s" };
    }

    return { tooltipUnit: WEIGHT_UNITS[units], axisYUnit: WEIGHT_UNITS[units] };
  }

  render() {
    const { from, to } = this.props;
    const { activeChart, chartData, unitLabel } = this.state;
    const { data, minIdx, maxIdx } = chartData;
    const { tooltipUnit } = unitLabel;
    const exercise = this.props.exercises[0];
    const { units, activeUser, intensityMeasure, isRiR } = this.props;
    const chartUnitLabel = units === "metric" ? "Kg" : "Lbs";

    const hideVelocitySettings = activeUser.hideVelocitySettings !== false;

    const showChange =
      !!data.length && ["load", "e1rm", "v1rm"].includes(activeChart);
    const showRM = exercise && ["primary", "secondary"].includes(exercise.type);
    const isVelocity = ["loadVelocity", "rpeVelocity"].includes(activeChart);

    const xAxisDomain =
      activeChart === "loadVelocity"
        ? [0, "auto"]
        : [convertToRiR(5, !isRiR), convertToRiR(10, !isRiR)];

    const THE_COLLECTOR = tooltipCollector();
   
    return (
      <>
        <section role="graph-wrapper">
          <div role="graph-title">
            <div role="graph-header">
              <h3>{!isVelocity ? tooltipUnit : ""}</h3>
            </div>
          </div>
          <div
            role="graph-body"
            className="graph-container"
            style={{
              position: "relative",
              display: isVelocity ? "none" : "block",
            }}
          >
            <div className="graph"></div>
            <div
              role="tooltip"
              className="animate__animated animate__fadeInUp"
              style={
                this.state.tooltip.show
                  ? {
                      top: this.state.tooltip.y,
                      left: this.state.tooltip.x,
                    }
                  : {
                      display: "none",
                    }
              }
              dangerouslySetInnerHTML={{
                __html: `<span>${this.state.tooltip.content} <b>${tooltipUnit}</b></span>`,
              }}
            ></div>
            <div role="graph-dates">
              <span>{moment(from).format("MMM D, YYYY")}</span>
              <span>{moment(to).format("MMM D, YYYY")}</span>
            </div>
          </div>

          {isVelocity && (
            <div style={{ width: "100%", height: data.length > 0 ? 500 : 316 }}>
              {data.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    width={500}
                    height={400}
                    data={data}
                    margin={{
                      top: 80,
                      right: 80,
                      bottom: 20,
                      left: 50,
                    }}
                    id={`velocity-${activeChart}`}
                  >
                    <Tooltip
                      cursor={false}
                      content={
                        <CustomTooltip
                          THE_COLLECTOR={THE_COLLECTOR}
                          unit={
                            activeChart === "loadVelocity"
                              ? "Load"
                              : intensityMeasure
                          }
                          units={units}
                        />
                      }
                    />
                    <Legend verticalAlign="top" align="right" />

                    <XAxis
                      dataKey="param"
                      type="number"
                      label={{
                        value:
                          activeChart === "loadVelocity"
                            ? `Load (${chartUnitLabel})`
                            : `${intensityMeasure}`,
                        position: "insideBottom",
                        offset: -10,
                      }}
                      domain={xAxisDomain}
                      ticks={
                        activeChart === "loadVelocity"
                          ? []
                          : [
                              convertToRiR(5, !isRiR),
                              convertToRiR(5.5, !isRiR),
                              convertToRiR(6, !isRiR),
                              convertToRiR(6.5, !isRiR),
                              convertToRiR(7, !isRiR),
                              convertToRiR(7.5, !isRiR),
                              convertToRiR(8, !isRiR),
                              convertToRiR(8.5, !isRiR),
                              convertToRiR(9, !isRiR),
                              convertToRiR(9.5, !isRiR),
                              convertToRiR(10, !isRiR),
                            ]
                      }
                    />
                    <YAxis
                      unit="m/s"
                      type="number"
                      dataKey="y"
                      label={{
                        value: "Mean Velocity (m/s)",
                        angle: -90,
                        position: "insideLeft",
                        offset: -15,
                      }}
                      tick={<CustomizedTick THE_COLLECTOR={THE_COLLECTOR} />}
                    />
                    <Scatter
                      name="Velocity"
                      dataKey="y"
                      fill="#fafafade"
                      strokeWidth={8}
                    />
                    <Line
                      name="Regression Line"
                      dataKey="line"
                      stroke={"#fafafa99"}
                      dot={false}
                      activeDot={false}
                      legendType="line"
                      strokeWidth={2}
                    />
                    <Line
                      name="Minimum Velocity Threshold"
                      dataKey="mvt"
                      stroke={"#fafafa99"}
                      dot={false}
                      activeDot={false}
                      legendType="plainline"
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      connectNulls
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <span>No velocity data to show</span>
                </div>
              )}
            </div>
          )}
        </section>
        <div className="head">
          <div>
            <div className="filter-buttons">
              {chartTypes.map(({ value, label }) => {
                if (
                  ["avgIntensity", "peakIntensity"].includes(value) &&
                  !showRM
                )
                  return null;

                if (
                  ["loadVelocity", "v1rm"].includes(value) &&
                  (!showRM || hideVelocitySettings)
                )
                  return null;

                return (
                  <button
                    onClick={(e) => {
                      this.toggleActiveChart(e);
                      this.props.onActiveChartChange(label);
                    }}
                    data-button-type={
                      activeChart === value ? "outline" : "secondary"
                    }
                    value={value}
                    key={`${value}selector`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {showChange && (
            <div className="bar-container">
              <div>
                <span>
                  <b>
                    {data[minIdx].y} {tooltipUnit}
                  </b>
                </span>
                <h4>Min</h4>
              </div>

              <div data-middle="true">
                <span>
                  <b>
                    {data[maxIdx].y} {tooltipUnit}
                  </b>
                </span>
                <h4>Max</h4>
              </div>

              <div>
                <span>
                  <b>
                    {maxIdx > minIdx ? "+" : maxIdx < minIdx ? "-" : ""}
                    {round(data[maxIdx].y - data[minIdx].y, 1)} {tooltipUnit}
                  </b>
                </span>
                <h4>Change</h4>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }
}

const tooltipCollector = () => {
  const _collection = [];

  let _min = { y: 0, value: 0 };
  let _max = { y: 1, value: 1 };

  const _setMaxAndMin = () => {
    const ys = _collection.map((obj) => obj.y);
    const maxY = Math.max(...ys);
    const maxYIndex = ys.indexOf(maxY);
    _max = _collection[maxYIndex];
    const minY = Math.min(...ys);
    const minYIndex = ys.indexOf(minY);
    _min = _collection[minYIndex];
  };

  return {
    collect: (value, y) => {
      _collection.push({ value, y });
      _setMaxAndMin(_collection);
    },
    maxAndMin: () => {
      return {
        max: { ..._max },
        min: { ..._min },
      };
    },
  };
};

const CustomizedTick = (props) => {
  const { x, y, payload, THE_COLLECTOR } = props;

  THE_COLLECTOR.collect(payload.value, y);

  if (payload.value !== 0) {
    return (
      <g>
        <text x={x} y={y} fill="#5d6571" textAnchor="end" dy={6}>
          {payload.value}
        </text>
      </g>
    );
  }

  return <g></g>;
};

const CustomTooltip = ({
  active,
  payload,
  unit,
  label,
  coordinate,
  THE_COLLECTOR,
  units,
}) => {
  // the condition for the length of the payload is for the first point of the line
  // as that one only has a target of 0, making the lenth 1

  if (
    active &&
    payload &&
    payload[0].dataKey !== "line" &&
    payload[0].dataKey !== "mvt"
  ) {
    const chartUnitLabel = units === "metric" ? "Kg" : "Lbs";

    const { min, max } = THE_COLLECTOR.maxAndMin();

    const threshold = min.value / 30;
    const deltaY = max.y - min.y;
    const deltaValue = max.value - min.value;
    const cursorValue =
      min.value - deltaValue * ((min.y - coordinate.y) / deltaY);

    const points = payload.map((p) => {
      const { color, dataKey, fill, name, payload } = p;
      return {
        color,
        dataKey,
        fill,
        name: name,
        value: payload[dataKey],
        meta: payload["meta"],
      };
    });

    const nearestPointIndexes = points.reduce((acc, curr, index) => {
      const deltaValue = Math.abs(curr.value - cursorValue);
      if (acc.length === 0)
        return deltaValue < threshold ? [{ index, deltaValue }] : [];
      if (Math.abs(deltaValue - acc[0].deltaValue) < threshold)
        return acc.concat([{ index, deltaValue }]);
      return acc;
    }, []);

    if (nearestPointIndexes.length === 0) return null;

    const nearestPoints = nearestPointIndexes.map(({ index }) => points[index]);

    return (
      <div className="custom-tooltip">
        <p className="label">Velocity Data</p>
        {nearestPoints.map((nearestPoint, index) => (
          <div key={`nearestPoint_${index}`}>
            <p className="desc">Date: {nearestPoint.meta}</p>
            <p>{`${nearestPoint.name}: ${nearestPoint.value}m/s`}</p>
            <p>
              {unit}: {round(label, 1)} {unit === "Load" ? chartUnitLabel : ""}
            </p>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export default HistoryChart;
